import React, { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { ChevronRight, Globe, Mail, MapPin, Menu as MenuIcon, UserRound, X } from "lucide-react";
import { BrandMark } from "../components/Logo";
import UnitSwitcher from "../components/UnitSwitcher";
import { adminSession } from "../api/client";

const Facebook = ({ size = 15 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M13.5 21v-7h2.4l.4-3h-2.8V9.1c0-.9.3-1.5 1.6-1.5h1.3V4.9c-.2 0-1-.1-1.9-.1-1.9 0-3.3 1.2-3.3 3.4V11H8.5v3h2.7v7h2.3Z" />
  </svg>
);
const Instagram = ({ size = 15 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <rect x="2" y="2" width="20" height="20" rx="5" />
    <circle cx="12" cy="12" r="4" />
    <circle cx="17.5" cy="6.5" r="0.5" fill="currentColor" />
  </svg>
);
const Youtube = ({ size = 15 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M21.6 7.2a2.5 2.5 0 0 0-1.8-1.8C18.3 5 12 5 12 5s-6.3 0-7.8.4A2.5 2.5 0 0 0 2.4 7.2 26 26 0 0 0 2 12a26 26 0 0 0 .4 4.8 2.5 2.5 0 0 0 1.8 1.8c1.5.4 7.8.4 7.8.4s6.3 0 7.8-.4a2.5 2.5 0 0 0 1.8-1.8A26 26 0 0 0 22 12a26 26 0 0 0-.4-4.8ZM10 15.2V8.8L15.5 12 10 15.2Z" />
  </svg>
);

const nav = [
  // The logo has always linked home; a labelled way back does not assume so.
  { to: "/", label: "Home", end: true },
  { to: "/competitions", label: "Events" },
  { to: "/competition-list", label: "Competitions" },
  { to: "/matches", label: "Matches" },
  { to: "/rankings", label: "Rankings" },
  { to: "/search", label: "Search" },
  { to: "/about", label: "About" },
];

export function Breadcrumb({ items }: { items: { label: string; to?: string }[] }) {
  return (
    <nav className="flex flex-wrap items-center gap-1.5 text-[13px] text-muted">
      {items.map((it, i) => (
        <React.Fragment key={i}>
          {i > 0 && <ChevronRight size={13} className="text-slate-300" />}
          {it.to ? (
            <Link to={it.to} className="transition-colors hover:text-primary">
              {it.label}
            </Link>
          ) : (
            <span className="font-medium text-ink">{it.label}</span>
          )}
        </React.Fragment>
      ))}
    </nav>
  );
}

export default function PublicLayout() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  // Close the mobile menu whenever the route changes.
  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <div className="flex min-h-screen flex-col bg-page">
      <header className="sticky top-0 z-40 bg-navy-950">
        <div className="mx-auto flex h-12 max-w-[1400px] items-center justify-between gap-3 px-4 sm:px-5">
          <Link to="/" className="shrink-0">
            <BrandMark compact />
          </Link>
          <nav className="hidden items-center gap-0.5 lg:flex">
            {nav.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                className={({ isActive }) =>
                  `relative px-3 py-1.5 text-sm font-semibold transition-colors ${
                    isActive ? "text-white after:absolute after:inset-x-3 after:-bottom-1 after:h-0.5 after:rounded-full after:bg-primary" : "text-slate-300 hover:text-white"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            {/* Units reach every page, so the control lives in the header. */}
            <UnitSwitcher dark />
            {/* Straight into the panel when a session is already open — sending a
                signed-in administrator to the login form looks like being logged out. */}
            <Link
              to={adminSession.valid() ? "/admin" : "/admin/login"}
              className="inline-flex h-7 items-center gap-1.5 rounded-md border border-slate-600 px-2.5 text-xs font-semibold text-slate-300 transition-colors hover:bg-navy-800 hover:text-white"
            >
              <UserRound size={13} />
              <span className="hidden sm:inline">{adminSession.valid() ? "Admin Panel" : "Admin"}</span>
            </Link>
            <button
              onClick={() => setOpen((v) => !v)}
              className="-mr-1 rounded-md p-1.5 text-slate-300 transition-colors hover:bg-navy-800 hover:text-white lg:hidden"
              aria-label={open ? "Close menu" : "Open menu"}
              aria-expanded={open}
            >
              {open ? <X size={20} /> : <MenuIcon size={20} />}
            </button>
          </div>
        </div>

        {/* Collapsed navigation for phones and tablets */}
        {open && (
          <nav className="border-t border-navy-800 px-4 pb-3 pt-1 lg:hidden">
            {nav.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                className={({ isActive }) =>
                  `block rounded-lg px-3 py-2.5 text-sm font-semibold transition-colors ${
                    isActive ? "bg-navy-800 text-white" : "text-slate-300 hover:bg-navy-800 hover:text-white"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>
        )}
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      <footer className="bg-navy-950 text-slate-300">
        <div className="mx-auto grid max-w-[1400px] gap-8 px-5 py-7 md:grid-cols-2 lg:grid-cols-4">
          <div>
            <BrandMark compact />
            <p className="mt-4 max-w-[240px] text-[13px] leading-relaxed text-slate-400">
              Official shooting competition results portal. Accurate. Reliable. Real-time.
            </p>
            {/* <div className="mt-5 flex gap-3">
              {[Facebook, Instagram, Youtube, Mail].map((Icon, i) => (
                <span key={i} className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full bg-navy-800 transition-colors hover:bg-primary">
                  <Icon size={15} />
                </span>
              ))}
            </div> */}
          </div>
          <div>
            <h4 className="mb-3 text-xs font-bold uppercase tracking-widest text-white">Quick Links</h4>
            <ul className="space-y-2 text-[13px]">
              {nav.map((n) => (
                <li key={n.to}>
                  <Link to={n.to} className="transition-colors hover:text-white">{n.label}</Link>
                </li>
              ))}
              <li>
                <Link
                  to={adminSession.valid() ? "/admin" : "/admin/login"}
                  className="font-semibold text-slate-200 transition-colors hover:text-white"
                >
                  {adminSession.valid() ? "Admin Panel" : "Admin Login"}
                </Link>
              </li>
            </ul>
          </div>
          <div>
            <h4 className="mb-4 text-xs font-bold uppercase tracking-widest text-white">Contact</h4>
            <ul className="space-y-3 text-sm">
              {/*
                The Association's own details, and nothing invented. The phone
                number and inbox that used to sit here were placeholders that
                nobody answers — worse than no contact at all, because a reader
                would have tried them. GRS publishes its own, and they change
                without telling us, so the website is the one thing to point at.
              */}
              <li className="flex gap-2.5">
                <MapPin size={15} className="mt-0.5 shrink-0 text-primary" />
                Sydney, New South Wales, Australia
              </li>
              <li className="flex items-center gap-2.5">
                <Globe size={15} className="shrink-0 text-primary" />
                <a
                  href="https://www.globalrangesolutions.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="transition-colors hover:text-white"
                >
                  www.globalrangesolutions.com
                </a>
              </li>
            </ul>
          </div>
          <div>
            <h4 className="mb-4 text-xs font-bold uppercase tracking-widest text-white">Results Status</h4>
            <p className="flex items-center gap-2 text-sm font-semibold text-emerald-400">
              <span className="h-2 w-2 rounded-full bg-emerald-400" /> All Systems Operational
            </p>
            <p className="mt-2 text-[13px] text-slate-400">Data updated regularly.<br />Please refresh for latest results.</p>
            <p className="mt-4 text-[13px] text-slate-500">Version 1.0.0</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
